import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { isServer } from './isServer'
import { Helmet } from 'react-helmet'
import { getPageContent } from './api'
import { serverData } from './serverData'

// Suburb authority page.
// Content comes from the KPod-managed `suburbs` contents row. The server
// fetches the record and renders it into the HTML (read from serverData);
// the client reads the #suburbdata island on hydration, and falls back to
// fetching the row by slug on client-side navigation.

// Prettify a slug into a display name for nearby-suburb links,
// e.g. "point-chevalier" -> "Point Chevalier".
const slugToName = (slug) =>
  (slug || '')
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

const readIslandRecord = () => {
  if (isServer) return undefined // server uses serverData (see initial state)
  const islandEl = document.getElementById('suburbdata')
  if (islandEl && islandEl.innerHTML.trim()) {
    try {
      const parsed = JSON.parse(islandEl.innerHTML)
      if (parsed === null) return null      // server resolved this slug as not-found
      if (parsed && !Array.isArray(parsed)) return parsed // single record
    } catch (e) {
      // fall through to API fetch
    }
  }
  return undefined // resolving (client-side nav) — fetch from API
}

const SuburbPage = props => {
  const { name } = useParams()

  const [data, setData] = useState(() =>
    isServer
      ? (serverData.suburbRecord !== undefined ? serverData.suburbRecord : null)
      : readIslandRecord()
  )

  // Client-side nav fallback: fetch the row and find this slug
  useEffect(() => {
    if (data === undefined) {
      getPageContent('suburbs', (d) => {
        let found = null
        if (d && d.length && d[0].value) {
          try {
            const obj = JSON.parse(d[0].value)
            found = (name && obj[name]) ? obj[name] : null
          } catch (e) {
            found = null
          }
        }
        setData(found)
      }, () => setData(null))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name])

  if (data === undefined) {
    // Resolving on the client — render nothing to avoid a flash of "not found"
    return <div className="main-body"><div className="column" /></div>
  }

  if (!data) {
    return (
      <div className="main-body">
        <div className="column">
          <h1 className="main-page-header">Suburb not found</h1>
          <p>
            We couldn't find that suburb guide.{' '}
            <Link to="/property">Browse all properties</Link> or{' '}
            <Link to="/ourpeople">meet the team</Link>.
          </p>
        </div>
      </div>
    )
  }

  // Build JSON-LD: Place + FAQPage (matches what the server injects)
  const placeSchema = {
    "@context": "https://schema.org",
    "@type": "Place",
    "name": `${data.name}, ${data.region}`,
    "description": data.headline,
    "url": `https://www.kellands.co.nz/suburbs/${data.slug}`,
  }

  const faqSchema = data.faqs && data.faqs.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": data.faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  } : null

  return (
    <div className="main-body">
      <Helmet>
        <meta charSet="utf-8" />
        <title>{data.metaTitle}</title>
        <meta name="description" content={data.metaDescription} />
        <meta property="og:title" content={data.metaTitle} />
        <meta property="og:description" content={data.metaDescription} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`https://www.kellands.co.nz/suburbs/${data.slug}`} />
        <script type="application/ld+json">{JSON.stringify(placeSchema)}</script>
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Helmet>

      <div className="column suburb-page">
        <h1 className="main-page-header">{data.name}</h1>

        {/* Suburb image gallery (up to 3) */}
        {data.images && data.images.filter(Boolean).length > 0 && (
          <div className="suburb-gallery">
            {data.images.filter(Boolean).slice(0, 3).map((src, i) => (
              <div className="suburb-gallery-item" key={i}>
                <img src={src} alt={`${data.name} — Kellands Real Estate`} loading={i === 0 ? undefined : 'lazy'} />
              </div>
            ))}
          </div>
        )}

        <p className="suburb-headline">{data.headline}</p>

        {/* Overview */}
        <section className="suburb-section">
          <h2>Suburb overview</h2>
          {data.intro.map((para, i) => <p key={i}>{para}</p>)}
        </section>

        {/* Who buys here */}
        {data.whoBuysHere && (
          <section className="suburb-section">
            <h2>Who buys here</h2>
            <p>{data.whoBuysHere}</p>
          </section>
        )}

        {/* Property types & prices */}
        {(data.propertyTypes || data.priceRanges) && (
          <section className="suburb-section">
            <h2>Property types &amp; price range</h2>
            {data.propertyTypes && (
              <ul className="suburb-list">
                {data.propertyTypes.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            )}
            {data.priceRanges && data.priceRanges.length > 0 && (
              <table className="suburb-price-table">
                <thead>
                  <tr><th>Property type</th><th>Indicative range</th></tr>
                </thead>
                <tbody>
                  {data.priceRanges.map((p, i) => (
                    <tr key={i}><td>{p.type}</td><td>{p.range}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
            {data.priceNote && <p className="suburb-note">{data.priceNote}</p>}
          </section>
        )}

        {/* Schools */}
        {data.schools && (
          <section className="suburb-section">
            <h2>School zones &amp; education</h2>
            {data.schools.primary && data.schools.primary.length > 0 && (
              <>
                <h3>Primary</h3>
                <ul className="suburb-list">
                  {data.schools.primary.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </>
            )}
            {data.schools.intermediate && data.schools.intermediate.length > 0 && (
              <>
                <h3>Intermediate</h3>
                <ul className="suburb-list">
                  {data.schools.intermediate.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </>
            )}
            {data.schools.secondary && data.schools.secondary.length > 0 && (
              <>
                <h3>Secondary</h3>
                <ul className="suburb-list">
                  {data.schools.secondary.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </>
            )}
            <p className="suburb-note">
              School zones are subject to change. Please check directly with the school.
            </p>
          </section>
        )}

        {/* Lifestyle */}
        {data.lifestyle && data.lifestyle.length > 0 && (
          <section className="suburb-section">
            <h2>Lifestyle &amp; local amenities</h2>
            <ul className="suburb-list">
              {data.lifestyle.map((l, i) => <li key={i}>{l}</li>)}
            </ul>
          </section>
        )}

        {/* What's selling now */}
        {data.sellingNow && (
          <section className="suburb-section">
            <h2>What's selling well right now</h2>
            <p>{data.sellingNow}</p>
          </section>
        )}

        {/* FAQ */}
        {data.faqs && data.faqs.length > 0 && (
          <section className="suburb-section">
            <h2>Frequently asked questions</h2>
            {data.faqs.map((f, i) => (
              <div className="suburb-faq" key={i}>
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </div>
            ))}
          </section>
        )}

        {/* Nearby suburbs */}
        {data.nearbySuburbs && data.nearbySuburbs.length > 0 && (
          <section className="suburb-section">
            <h2>Nearby suburb guides</h2>
            <ul className="suburb-list suburb-nearby">
              {data.nearbySuburbs.map(slug => (
                <li key={slug}>
                  <Link to={`/suburbs/${slug}`}>{slugToName(slug)}</Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* CTA */}
        <section className="suburb-section suburb-cta">
          <h2>Thinking of selling in {data.name}?</h2>
          <p>
            The Kellands team has deep local experience across {data.name} and
            Auckland's city fringe.{' '}
            <Link to="/ourpeople">Meet the team</Link> or{' '}
            <Link to="/property">browse current listings</Link>.
          </p>
          <p>
            <Link to="/suburbs" className="suburb-all-link">View all suburb guides</Link>
          </p>
        </section>
      </div>
    </div>
  )
}

export default SuburbPage
